import i18n from "../i18n";

export function extractTextFromValue(val) {
  if (val == null || val === "") return "";
  if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
    return String(val);
  }
  if (Array.isArray(val)) {
    return val.map(extractTextFromValue).filter(Boolean).join(", ");
  }
  if (typeof val === "object") {
    if (val.props?.alt) return val.props.alt;
    if (val.props?.title) return val.props.title;
    if (val.props?.children) {
      if (Array.isArray(val.props.children)) {
        return val.props.children.map(extractTextFromValue).filter(Boolean).join(" ");
      }
      return extractTextFromValue(val.props.children);
    }
    if (val.display !== undefined) return `${val.label || ""}: ${val.display}`;
    if (val.name) return val.name;
    try {
      return JSON.stringify(val);
    } catch (e) {
      return "";
    }
  }
  return "";
}

export function formatCSVValue(value, headerKey = "") {
  let text = extractTextFromValue(value).trim();
  if (!text) return '""';

  if (i18n && typeof i18n.t === "function" && isNaN(Number(text)) && !text.includes(":")) {
    text = i18n.t(text, { defaultValue: text });
  }

  const isPureNumber = /^\d+$/.test(text);
  const isPhoneOrIdKey = /phone|mobile|imei|vin|dl|license|reg|serial|id|code/i.test(headerKey);
  const startsWithZero = isPureNumber && /^0\d+$/.test(text);
  const isLongNumber = isPureNumber && text.length >= 7;

  if (isLongNumber || startsWithZero || (isPhoneOrIdKey && isPureNumber)) {
    return '"=""' + text + '"""';
  }

  // Standard CSV double quotes escaping for all normal strings
  const escaped = text.replace(/"/g, '""');
  return '"' + escaped + '"';
}

export function getValueFromRow(row, key) {
  if (!row || !key) return "";

  // 1. Direct property lookup
  let val = row[key];

  // 2. Specific key fallbacks for common domain fields
  if (val === undefined) {
    const lowerKey = String(key).toLowerCase();

    if (lowerKey.includes("driver")) {
      val =
        row.driver ||
        row.driverName ||
        (row.driverPhone ? `${row.driverName || "N/A"} (${row.driverPhone})` : row.driverName) ||
        row.originalData?.driverName ||
        (row.originalData?.driver
          ? `${row.originalData.driver.first_name || ""} ${row.originalData.driver.last_name || ""}`.trim()
          : undefined);
    } else if (lowerKey.includes("vehicle") || lowerKey === "name") {
      val = row.vehicleName || row.vehicle || row.name || row.VehicleName || row.originalData?.name;
    } else if (lowerKey.includes("reg")) {
      val = row.regNo || row.VehicleNumber || row.vehicleNumber || row.originalData?.VehicleNumber;
    } else if (lowerKey.includes("live")) {
      if (Array.isArray(row.liveDataCards) && row.liveDataCards.length) {
        val = row.liveDataCards
          .map((c) => (c.display != null ? `${c.label}: ${c.display}` : c.label))
          .join(", ");
      } else {
        val = row.liveData || "--";
      }
    } else if (lowerKey.includes("location")) {
      val = row.currentLocation || row.location || row.originalData?.location;
    } else if (lowerKey.includes("update")) {
      val = row.lastUpdate || row.updatedAt || row.originalData?.lastUpdate;
    } else if (lowerKey.includes("status")) {
      val = row.status || row.originalStatus || row.originalData?.status;
    } else if (lowerKey.includes("route")) {
      val = row.route || row.routeName || row.originalData?.route?.name;
    }
  }

  // 3. Dot notation support (e.g. "organization.name")
  if (val === undefined && key.includes(".")) {
    const parts = key.split(".");
    let curr = row;
    for (const p of parts) {
      if (curr == null) break;
      curr = curr[p];
    }
    val = curr;
  }

  // 4. Case-insensitive key lookup in row
  if (val === undefined) {
    const lowerKey = String(key).toLowerCase();
    for (const k of Object.keys(row)) {
      if (k.toLowerCase() === lowerKey) {
        val = row[k];
        break;
      }
    }
  }

  // 5. Fallback lookup in originalData object
  if (val === undefined && row.originalData) {
    val = row.originalData[key];
  }

  return val == null ? "" : val;
}

export function exportTableToCSV(filename = "download-data", headers = [], data = []) {
  if (!Array.isArray(data) || data.length === 0) {
    console.warn("No data available to export");
    return;
  }

  // Filter out actions and internal columns
  const validHeaders = (headers || [])
    .map((h) => {
      if (typeof h === "string") return { key: h, label: h };
      const key = h.key || h.id || h.value || h.accessorKey;
      const label = h.label || h.value || h.header || h.name || key;
      return { key, label };
    })
    .filter(
      (h) =>
        h.key &&
        !["actions", "action", "select", "checkbox", "_actions", "ACTIONS_COLUMN_ID"].includes(
          String(h.key).toLowerCase()
        )
    );

  // If no headers supplied, infer from first row keys
  const cols =
    validHeaders.length > 0
      ? validHeaders
      : Object.keys(data[0] || {})
        .filter((k) => !["id", "originalData", "actions", "_actions", "liveDataCards"].includes(k))
        .map((k) => ({ key: k, label: k }));

  // Build CSV Header line with active language translations
  const headerLine = cols
    .map((c) => {
      const rawLabel = String(c.label || c.key);
      const translatedLabel =
        i18n && typeof i18n.t === "function"
          ? i18n.t(rawLabel, { defaultValue: rawLabel })
          : rawLabel;
      return `"${translatedLabel.replace(/"/g, '""')}"`;
    })
    .join(",");

  // Build CSV Data lines
  const dataLines = data.map((row) => {
    return cols
      .map((col) => {
        const val = getValueFromRow(row, col.key);
        return formatCSVValue(val, col.key);
      })
      .join(",");
  });

  const csvContent = [headerLine, ...dataLines].join("\r\n");

  // Add UTF-8 BOM so Excel opens file with proper encoding and column alignment
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const cleanFileName = (filename || "download-data")
    .replace(/[^a-z0-9_-]/gi, "_")
    .toLowerCase();

  link.href = url;
  link.setAttribute("download", `${cleanFileName}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default exportTableToCSV;
